import React, { useState } from "react";
import {
  Box,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import "../Components/promotion.css";
import InventoryIcon from "@mui/icons-material/Inventory";
import PercentIcon from "@mui/icons-material/Percent";
import ViewCompactIcon from "@mui/icons-material/ViewCompact";
import ItemSelectionModal from "./ItemSelectionModal";

const columns = [
  {
    key: "CategoryName",
    header: "Category",
    align: "left",
    width: 160,
    // flex: 1, // will expand if space is available
    render: (row, col) => (
      <Stack direction="row" alignItems="center" spacing={1}>
        <InventoryIcon color="primary" fontSize="small" />
        <Typography fontWeight={600}>{row.CategoryName}</Typography>
      </Stack>
    ),
  },
  {
    key: "SubGrpName",
    header: "Sub Group",
    align: "left",
    width: 140,
    render: (row) => <Typography color="primary">{row.SubGrpName}</Typography>,
  },
  {
    key: "MainGrpName",
    header: "Main Group",
    align: "left",
    width: 140,
    render: (row) => <Typography>{row.MainGrpName}</Typography>,
  },
  {
    key: "BuyerName",
    header: "Buyer",
    align: "left",
    width: 140,
    render: (row) => <Typography color="primary">{row.BuyerName}</Typography>,
  },
  {
    key: "BrandName",
    header: "Brand",
    align: "left",
    width: 140,
    render: (row, col) =>
      row.BrandName ? (
        <Chip
          label={row.BrandName}
          variant="outlined"
          color="default"
          size="small"
          sx={{ borderRadius: "999px" }}
        />
      ) : (
        ""
      ),
  },
  {
    key: "Packing",
    header: "Packing",
    align: "center",
    width: 100,
    render: (row, col) =>
      row.Packing ? (
        <Typography>
          <ViewCompactIcon
            color="warning"
            fontWeight="bold"
            fontSize="small"
            sx={{ mr: 0.5, verticalAlign: "middle" }}
          />
          {row.Packing}
        </Typography>
      ) : (
        ""
      ),
  },
  {
    key: "NoOfItems",
    header: "No.Of Items",
    align: "center",
    width: 120,
    render: (row) => <Typography color="orchid">{row.NoOfItems}</Typography>,
  },
  {
    key: "NoOfItemsSelected",
    header: "Items Selected ",
    align: "center",
    minWidth: 90,
    render: (row) => (
      <Typography color="goldenrod">{row.NoOfItemsSelected}</Typography>
    ),
  },
  {
    key: "NoOfItemsSelectedPerc",
    header: "Items Selected %",
    align: "right",
    width: 100,
    render: (row) => (
      <Stack
        direction="row"
        alignItems="right"
        justifyContent="right"
        spacing={0.5}
      >
        <PercentIcon fontSize="small" color="success" />
        <Typography color="success">{row.NoOfItemsSelectedPerc}</Typography>
      </Stack>
    ),
  },
];

// helper to apply width/minWidth/flex consistently
const cellSx = (col) => {
  const sx = {
    whiteSpace: "nowrap", // keep in one line; enables horizontal scroll
  };
  if (col.width) sx.width = col.width; // px or %
  if (col.minWidth) sx.minWidth = col.minWidth; // px
  if (col.flex) sx.width = `${col.flex * 100}%`; // simple flex-like %
  return sx;
};

export default function OrdersPage({ rows, data, pageType, fGetRow, loadSummary }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isPopModalDataChanged, setIsPopModalDataChanged] = useState(false);

  const handleRowClick = (row) => {
    setSelectedRow(row);
    setModalOpen(true);

    setTimeout(() => {
      document.getElementsByName('ItmName')[0]?.focus();
    }, 400);
  };

  const handleModalClose = async () => {
    setModalOpen(false);
    if (isPopModalDataChanged) {
      await fGetRow();
      await loadSummary();
    }
    setIsPopModalDataChanged(false);
  };

  return (
    <Box className="accordian">
      <TableContainer
        sx={{
          borderRadius: 2,
          maxHeight: 420,
          overflowY: "auto",
          overflowX: "auto",
          scrollbarWidth: "thin",
          scrollbarColor: "#c2e3faff transparent",
          "&::-webkit-scrollbar": {
            height: 8,
            width: 8,
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#4e8fbd",
            borderRadius: 4,
          },
        }}
      >
        <Table
          stickyHeader
          size="small"
          sx={{
            width: "100%",
            tableLayout: "auto",
            borderCollapse: "separate",
            borderSpacing: "0 8px", // 1) gap between rows
            [`& .${tableCellClasses.root}`]: { borderBottom: "none" }, // remove default borders
          }}
        >
          <TableHead
            sx={{
              "& th": {
                fontWeight: 700,
                color: "black",
                // bgcolor: "primary.main",
                p: 1,

                borderBottom: "none",
              },
              "& .MuiTableCell-root": {
                backgroundColor: "#0d73bbc2 !important",
                color: "white",
              },
            }}
          >
            <TableRow sx={{ bgcolor: "grey.100" }}>
              {columns?.map((col) => (
                <TableCell
                  key={col.key}
                  align={col.align || "left"}
                  sx={{ fontWeight: 700, ...cellSx(col) }}
                >
                  {col.header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody
            sx={{
              "& tr": {
                boxShadow: "0 2px 12px rgba(0,0,0,0.06) !important",
                padding: "8px !important",
              },
              "& .MuiTableRow-root:hover": {
                boxShadow: "4px 6px 18px rgba(0,0,0,0.08) !important",
                transition: "all 0.4s ease",
                cursor: "pointer", // light blue hover color
              },
            }}
          >
            {!rows || rows?.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  align="center"
                  sx={{
                    py: 4,
                    color: "grey.600",
                    fontSize: "16px",
                    fontWeight: 600,
                    bgcolor: "#fafafa",
                  }}
                >
                  No category available
                </TableCell>
              </TableRow>
            ) : (
              rows?.map((row, idx) => (
                <TableRow
                  key={idx}
                  hover
                  onClick={() => handleRowClick(row)}
                  sx={{
                    "& td": {
                      bgcolor: "white",
                      padding: 1,
                      color: "#141414 !important",
                    },
                    "& td:first-of-type": {
                      borderTopLeftRadius: 12,
                      borderBottomLeftRadius: 12,
                    },
                    "& td:last-of-type": {
                      borderTopRightRadius: 12,
                      borderBottomRightRadius: 12,
                    },
                  }}
                >
                  {columns?.map((col) => (
                    <TableCell
                      key={col.key}
                      align={col.align || "left"}
                      sx={cellSx(col)}
                    >
                      {col.render ? col.render(row, col) : row[col.key]}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {pageType === "Items" && (
        <ItemSelectionModal
          open={modalOpen}
          onClose={handleModalClose}
          selectedRow={selectedRow}
          data={data}
          setIsPopModalDataChanged={setIsPopModalDataChanged}
        />
      )}
    </Box>
  );
}
